import { useWindowSize } from "hooks/useWindowSize";
import { CHECKLIST_ITEM_SECTION } from "constants/financialClosing";
import { FieldArray, getIn, useFormikContext } from "formik";
import {
  ChecklistItem,
  useGetFinancialClosingQuery,
  useUpdateChecklistMutation,
} from "store/apis/financialClosing";
import { CheckListAccordion } from "./CheckListAccordion";
import {
  FINANCIAL_CLOSING_CHECKLIST_LABEL,
  FINANCIAL_CLOSING_CHECKLIST_TOOLTIP_TEXT,
} from "dictionaries";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useParams } from "react-router-dom";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import classNames from "classnames";
import React, { ComponentProps } from "react";
import RadioGroup from "components/DesignSystem/RadioGroup/RadioGroup";
import Card from "components/DesignSystem/Card/Card";
import { Label, TextInput } from "components/DesignSystem/TextInput/TextInput";
import Modal from "components/DesignSystem/Modal/Modal";
import { Button } from "components/DesignSystem/Button/Button";
import { FormikForm } from "components/FormikForm/FormikForm";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { ArrowRight } from "components/icons/ArrowRight";
import { useToast } from "hooks/useToast";
import { BackendError } from "types/utils/error";
import { TrashSimple } from "components/icons/TrashSimple";
import { PlusIcon } from "components/icons/PlusIcon";
import { Info } from "./CheckList";
import ToolTip from "components/design/toolTip";
import { closingCustomChecklistSchema } from "formValidations/closingCustomChecklistSchema";

const ChecklistTrigger = ({
  children,
  systemGenerated = true,
  selectedCount,
  totalCount,
  ...props
}: {
  children: React.ReactNode;
  systemGenerated?: boolean;
  selectedCount?: number;
  totalCount?: number;
} & Omit<ComponentProps<typeof CheckListAccordion.Trigger>, "onChange">) => {
  return (
    <div className="t-flex t-items-center t-gap-3 t-w-full">
      <CheckListAccordion.Trigger
        className="t-w-full !t-pt-3 data-state-open:!t-pb-2 data-state-closed:!t-pb-3"
        {...props}
      >
        <div className="t-flex-1">
          <div className="t-flex t-items-center t-justify-between">
            <div className="t-flex t-flex-col t-gap-1">
              <span className="t-rounded-lg">{children}</span>
              <span className="t-text-caption-italic t-italic t-text-text-30 ">
                {systemGenerated
                  ? "System generated checklist"
                  : "Manually added"}
              </span>
            </div>
            {/* @ts-ignore */}
            {Boolean(totalCount) && totalCount > 0 && (
              <div className="t-ml-auto t-text-body-sm t-text-text-30">
                {`${selectedCount}/${totalCount} selected`}
              </div>
            )}
          </div>
        </div>
      </CheckListAccordion.Trigger>
    </div>
  );
};

type FormType = {
  checklist_visibility_list: {
    checklist_item_id: string;
    is_visible: boolean;
  }[];
  other_actions: {
    uuid?: string;
    name: string;
    description: string;
    is_visible: boolean;
  }[];
};

const ChecklistOtherContent = () => {
  const { values, setFieldValue } = useFormikContext<FormType>();
  const { isMobile } = useWindowSize();
  const { other_actions } = values;

  return (
    <CheckListAccordion.Content>
      <FieldArray name="other_actions">
        {({ remove, push }) => (
          <div
            className={classNames(
              "t-border-t t-border-0 t-border-neutral-10 t-border-solid t-items-start",
              {
                "t-py-3": other_actions.length > 0,
                "t-pb-3": other_actions.length === 0,
              }
            )}
          >
            {other_actions.length > 0 && (
              <>
                <div className="t-grid t-gap-y-3 t-grid-cols-[32px,1fr,1fr,24px] t-items-center sm:t-hidden">
                  <div></div>
                  <Label required>Title</Label>
                  <Label>Description</Label>
                  <div></div>
                </div>

                <div className="t-grid t-gap-y-3 t-grid-cols-[32px,1fr,1fr,24px] t-items-start sm:t-grid-cols-[32px,1fr,24px] sm:t-gap-y-2">
                  {other_actions?.map((item, itemindex) => (
                    <React.Fragment key={item.uuid || itemindex}>
                      <div className="t-mr-3 t-pt-2">
                        <Checkbox
                          name={`other_actions.${itemindex}.is_visible`}
                          checked={other_actions[itemindex].is_visible || false}
                          onChange={(e) => {
                            setFieldValue(
                              `other_actions.${itemindex}.is_visible`,
                              e.target.checked
                            );
                          }}
                        />
                      </div>
                      <div className="t-mr-4 sm:t-mr-2 sm:t-col-start-2 sm:t-min-w-0">
                        <TextInput
                          customSize="small"
                          placeholder={isMobile ? "Title" : undefined}
                          name={`other_actions.${itemindex}.name`}
                        />
                      </div>
                      <div className="t-mr-4 sm:t-mr-2 sm:t-col-start-2 sm:t-row-start-2 sm:t-min-w-0">
                        <TextInput
                          customSize="small"
                          placeholder={isMobile ? "Description" : undefined}
                          name={`other_actions.${itemindex}.description`}
                        />
                      </div>

                      <div className="sm:t-col-start-3 sm:t-row-start-1">
                        <Button
                          customType="ghost_icon"
                          size="xsmall"
                          onClick={() => remove(itemindex)}
                        >
                          <span className="t-text-text-30">
                            <TrashSimple />
                          </span>
                        </Button>
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              </>
            )}

            <div className="t-mt-3">
              <Button
                size="small"
                onClick={() =>
                  push({ name: "", description: "", is_visible: true })
                }
              >
                <span className="t-flex t-items-center t-gap-1">
                  <PlusIcon />
                  <span>Add item to checklist</span>
                </span>
              </Button>
            </div>
          </div>
        )}
      </FieldArray>
    </CheckListAccordion.Content>
  );
};

const ChecklistContent = ({
  list,
  disabled,
}: {
  list: ChecklistItem[];
  disabled?: boolean;
}) => {
  const { values, setFieldValue } = useFormikContext<FormType>();

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFieldValue(name, checked);
  };

  const getIndex = (uuid: string) => {
    return values.checklist_visibility_list.findIndex(
      (item) => item.checklist_item_id === uuid
    );
  };

  return (
    <CheckListAccordion.Content>
      <div className="t-flex t-flex-col t-border-t t-border-0 t-border-neutral-10 t-border-solid t-py-3">
        {list?.map((item, itemindex) => {
          return (
            <Checkbox
              key={item.uuid}
              disabled={disabled}
              name={`checklist_visibility_list.${getIndex(
                item.uuid
              )}.is_visible`}
              label={
                <div className="t-flex t-justify-between t-py-2 t-items-center">
                  <div className="t-flex t-flex-col">
                    <div className="t-flex t-gap-1 t-text-subtext sm:t-text-subtext-sm t-text-text-60">
                      {FINANCIAL_CLOSING_CHECKLIST_LABEL[item.type]}
                      <Info
                        text={
                          FINANCIAL_CLOSING_CHECKLIST_TOOLTIP_TEXT[item.type]
                        }
                        side="right"
                      />
                    </div>
                  </div>
                </div>
              }
              checked={getIn(
                values,
                `checklist_visibility_list.${getIndex(item.uuid)}.is_visible`
              )}
              onChange={onChange}
            />
          );
        })}
      </div>
    </CheckListAccordion.Content>
  );
};

export const ManageClosingContent = () => {
  const { financialClosingId } = useParams<{ financialClosingId: string }>();
  const entityId = useCurrentEntityId();
  const { data: financialClosingDetails } = useGetFinancialClosingQuery({
    entityId: entityId,
    financialClosingId: financialClosingId,
  });

  const { values, setFieldValue, errors } = useFormikContext<FormType>();

  const otherActionErrors = getIn(errors, "other_actions");

  const { checklist_items } = financialClosingDetails || {
    checklist_items: [],
  };

  const reconciliationAccounts = checklist_items.filter(
    ({ section }) => section === CHECKLIST_ITEM_SECTION.RECONCILE_ACCOUNTS
  );

  const verifyCategorisation = checklist_items.filter(
    ({ section }) => section === CHECKLIST_ITEM_SECTION.VERIFY_CATEGORIZATION
  );

  const reviewChanges = checklist_items.filter(
    ({ section }) => section === CHECKLIST_ITEM_SECTION.REVIEW_CHANGES
  );

  const finalizeFinancials = checklist_items.filter(
    ({ section }) => section === CHECKLIST_ITEM_SECTION.FINALIZE_FINANCIALS
  );

  const otherActions = checklist_items.filter(
    ({ section }) => section === CHECKLIST_ITEM_SECTION.OTHER_ACTION
  );

  const getIndex = (uuid: string) => {
    return values.checklist_visibility_list.findIndex(
      (item) => item.checklist_item_id === uuid
    );
  };

  const reviewChangesValue = values.checklist_visibility_list.filter(
    (item, index) =>
      reviewChanges.some(
        (checklistItem) => checklistItem.uuid === item.checklist_item_id
      )
  );

  const selectedReviewChanges = reviewChangesValue.filter(
    (item) => item.is_visible === true
  );

  const verifyCategorisationValue = values.checklist_visibility_list.filter(
    (item, index) =>
      verifyCategorisation.some(
        (checklistItem) => checklistItem.uuid === item.checklist_item_id
      )
  );

  const selectedVerifyCategorisation = verifyCategorisationValue.filter(
    (item) => item.is_visible === true
  );

  const selectedOtherActions = values.other_actions.filter(
    (item) => item.is_visible === true
  );

  const isSectionVisible = (list: ChecklistItem[]) => {
    return list.some((item) => {
      const index = getIndex(item.uuid);
      return (
        index !== -1 &&
        values.checklist_visibility_list[index]?.is_visible === true
      );
    });
  };

  const onSectionChange = (list: ChecklistItem[]) => {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      list.forEach((item) => {
        const index = getIndex(item.uuid);
        if (index !== -1) {
          setFieldValue(
            `checklist_visibility_list.${index}.is_visible`,
            e.target.checked
          );
        }
      });
    };
  };

  const onOtherSectionChange = (list: ChecklistItem[]) => {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      list.forEach((item) => {
        const index = values.other_actions.findIndex(
          (action) => action.uuid === item.uuid
        );
        if (index !== -1) {
          setFieldValue(`other_actions.${index}.is_visible`, e.target.checked);
        }
      });
    };
  };

  const isAnyOtherActionsVisible = values.other_actions.some((action) => {
    return action.is_visible === true;
  });

  return (
    <div>
      <RadioGroup.Root>
        <CheckListAccordion.Root
          type="multiple"
          className="t-flex-col t-flex t-gap-4"
          defaultValue={[CHECKLIST_ITEM_SECTION.OTHER_ACTION]}
        >
          <div className="t-flex t-gap-3 sm:t-gap-2">
            <div className="t-mt-6 sm:t-mt-5">
              <Checkbox
                onChange={onSectionChange(reconciliationAccounts)}
                checked={isSectionVisible(reconciliationAccounts)}
              />
            </div>
            <Card.Root
              className="t-rounded-lg t-flex-1 sm:t-min-w-0 sm:!t-overflow-x-hidden !t-px-3"
              variant="primary"
            >
              <CheckListAccordion.Item
                value={CHECKLIST_ITEM_SECTION.RECONCILE_ACCOUNTS}
              >
                <ChecklistTrigger disabled>Reconcile Accounts</ChecklistTrigger>
              </CheckListAccordion.Item>
            </Card.Root>
          </div>

          <div className="t-flex t-gap-3 sm:t-gap-2">
            <div className="t-mt-6 sm:t-mt-5">
              <Checkbox
                onChange={onSectionChange(verifyCategorisation)}
                checked={isSectionVisible(verifyCategorisation)}
              />
            </div>
            <Card.Root
              className="t-rounded-lg t-flex-1 sm:t-min-w-0 sm:!t-overflow-x-hidden !t-px-3"
              variant="primary"
            >
              <CheckListAccordion.Root type="multiple">
                <CheckListAccordion.Item
                  className="t-gap-0"
                  value={CHECKLIST_ITEM_SECTION.VERIFY_CATEGORIZATION}
                >
                  <ChecklistTrigger
                    selectedCount={selectedVerifyCategorisation.length}
                    totalCount={verifyCategorisation.length}
                  >
                    Verify Categorisation
                  </ChecklistTrigger>
                  <ChecklistContent list={verifyCategorisation} />
                </CheckListAccordion.Item>
              </CheckListAccordion.Root>
            </Card.Root>
          </div>

          <div className="t-flex t-gap-3 sm:t-gap-2">
            <div className="t-mt-6 sm:t-mt-5">
              <Checkbox
                onChange={onSectionChange(reviewChanges)}
                checked={isSectionVisible(reviewChanges)}
              />
            </div>

            <Card.Root
              className="t-rounded-lg t-flex-1 sm:t-min-w-0 sm:!t-overflow-x-hidden !t-px-3"
              variant="primary"
            >
              <CheckListAccordion.Item
                value={CHECKLIST_ITEM_SECTION.REVIEW_CHANGES}
              >
                <ChecklistTrigger
                  selectedCount={selectedReviewChanges.length}
                  totalCount={reviewChanges.length}
                >
                  Review Changes
                </ChecklistTrigger>
                <ChecklistContent list={reviewChanges} />
              </CheckListAccordion.Item>
            </Card.Root>
          </div>

          <div className="t-flex t-gap-3 sm:t-gap-2">
            <div className="t-mt-6 sm:t-mt-5">
              <ToolTip text="This is a mandatory checklist item">
                <span>
                  <Checkbox disabled checked />
                </span>
              </ToolTip>
            </div>

            <Card.Root
              className="t-rounded-lg t-flex-1 sm:t-min-w-0 sm:!t-overflow-x-hidden !t-px-3"
              variant="primary"
            >
              <CheckListAccordion.Item
                value={CHECKLIST_ITEM_SECTION.FINALIZE_FINANCIALS}
              >
                <ChecklistTrigger
                  selectedCount={finalizeFinancials.length}
                  totalCount={finalizeFinancials.length}
                >
                  Finalize Financials
                </ChecklistTrigger>
                <ChecklistContent disabled list={finalizeFinancials} />
              </CheckListAccordion.Item>
            </Card.Root>
          </div>

          <div className="t-flex t-gap-3 sm:t-gap-2">
            <div className="t-mt-6 sm:t-mt-5">
              <Checkbox
                onChange={onOtherSectionChange(otherActions)}
                checked={isAnyOtherActionsVisible}
                disabled={!isAnyOtherActionsVisible}
              />
            </div>

            <Card.Root
              className="t-rounded-lg t-flex-1 sm:t-min-w-0 sm:!t-overflow-x-hidden !t-px-3"
              variant="primary"
            >
              <CheckListAccordion.Item
                value={CHECKLIST_ITEM_SECTION.OTHER_ACTION}
              >
                <ChecklistTrigger
                  systemGenerated={false}
                  selectedCount={selectedOtherActions.length}
                  totalCount={values.other_actions.length}
                >
                  Other Actions
                </ChecklistTrigger>
                <ChecklistOtherContent />
              </CheckListAccordion.Item>
            </Card.Root>
          </div>
        </CheckListAccordion.Root>
      </RadioGroup.Root>
    </div>
  );
};

export const ManageClosing = ({
  onClose,
  ...props
}: {
  onClose: () => void;
}) => {
  const { financialClosingId } = useParams<{ financialClosingId: string }>();
  const entityId = useCurrentEntityId();
  const { data: financialClosingDetails } = useGetFinancialClosingQuery({
    entityId: entityId,
    financialClosingId: financialClosingId,
  });

  const { successToast, alertToast } = useToast();

  const [updateChecklist, { isLoading: isUpdating }] =
    useUpdateChecklistMutation();

  const initialValues: FormType = {
    checklist_visibility_list:
      financialClosingDetails?.checklist_items
        .filter((item) => item.section !== CHECKLIST_ITEM_SECTION.OTHER_ACTION)
        .map((item) => ({
          checklist_item_id: item.uuid,
          is_visible: item.is_visible || false,
        })) || [],
    other_actions:
      financialClosingDetails?.checklist_items
        .filter((item) => item.section === CHECKLIST_ITEM_SECTION.OTHER_ACTION)
        .map((item) => ({
          uuid: item.uuid,
          name: item.name || "",
          description: item.description || "",
          is_visible: item.is_visible || false,
        })) || [],
  };

  const updateChecklistHandler = async (
    values: FormType,
    updateUpcoming: boolean
  ) => {
    try {
      await updateChecklist({
        entityId: entityId,
        financialClosingId: financialClosingId,
        payload: {
          checklist_visibility_list: values.checklist_visibility_list,
          other_actions: values.other_actions,
          update_upcoming_closing_also: updateUpcoming,
        },
      }).unwrap();
      successToast({ message: "Checklist updated successfully" });
      onClose();
    } catch (error) {
      alertToast({
        message:
          (error as BackendError).data?.error?.message ||
          "Failed to update checklist",
      });
    }
  };

  return (
    <FormikForm
      {...props}
      initialValues={initialValues}
      onSubmit={() => {}}
      validationSchema={closingCustomChecklistSchema}
    >
      {({ values, isValid, validateForm }) => (
        <>
          <Modal.Header>
            <Modal.Title>Manage Checklist</Modal.Title>
            <Modal.Close />
          </Modal.Header>
          <Modal.Body>
            <p className="t-text-body-sm t-text-text-30 t-mb-4">
              Select the parameters you wish to incorporate into your monthly
              financial closing. This checklist will serve as the default for
              future closings and can be modified at any time.
            </p>
            <ManageClosingContent />
          </Modal.Body>
          <Modal.FooterButtonGroup>
            <Modal.RawClose asChild>
              <Button customType="secondary" size="small">
                Cancel
              </Button>
            </Modal.RawClose>
            <Dropdown.Root>
              <Dropdown.Trigger asChild className="t-group">
                <div>
                  <Button
                    customType="primary"
                    size="small"
                    isLoading={isUpdating}
                  >
                    <div className="t-flex t-gap-1 t-items-center">
                      <span>Update checklist</span>
                      <div className="t-rotate-90 group-data-state-open:-t-rotate-90 t-transition-transform">
                        <ArrowRight stroke="2.5" color="currentColor" />
                      </div>
                    </div>
                  </Button>
                </div>
              </Dropdown.Trigger>
              <Dropdown.Content side="top" align="end" sideOffset={8}>
                <Dropdown.Item
                  onSelect={async () => {
                    const errors = await validateForm();
                    if (Object.keys(errors).length > 0) {
                      return;
                    }
                    // @ts-ignore
                    updateChecklistHandler(values, false);
                  }}
                >
                  Update this closing
                </Dropdown.Item>
                <Dropdown.Item
                  onSelect={async () => {
                    const errors = await validateForm();
                    if (Object.keys(errors).length > 0) {
                      return;
                    }
                    // @ts-ignore
                    updateChecklistHandler(values, true);
                  }}
                >
                  Update this and upcoming closings
                </Dropdown.Item>
              </Dropdown.Content>
            </Dropdown.Root>
          </Modal.FooterButtonGroup>
        </>
      )}
    </FormikForm>
  );
};
